import { useEffect, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import { useFieldTranslation } from 'src/components/fields/useFieldTranslation';
import { useUser } from 'src/components/hooks/useUser';
import { useDeleteQueryMutation, useUpdateQueryMutation } from 'src/services/Queries';
import { useCreateQueryBulkResponseMutation, useCreateQueryResponseMutation } from 'src/services/QueryResponse';
import { useAppSelector } from 'src/store/hooks';
import { hideLoader, setProgress } from 'src/store/Loader';
import { setMessage } from 'src/store/Message';
import { IMessage } from 'src/types/Message';
import { IDeleteQuery, IUpdateQuery } from 'src/types/Queries';
import { ICreateQueryBulResponse, ICreateQueryResponse } from 'src/types/QueryResponse';
import { useSelectedAppInfo } from 'src/util/SelectedAppInfo';

export const useViewQuery = (getSivReportQueries: () => void, fieldQueries: any, queryKeys: any, setOpen: any) => {
  const dispatch = useDispatch();
  const { selectedAppSection } = useSelectedAppInfo();
  const { user } = useUser();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [respondFieldQueries, setRespondFieldQueries] = useState(false);
  const [respondSectionQueries, setRespondSectionQueries] = useState(false);

  const handleChangeAccordion = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
    setRespondFieldQueries(false);
    setRespondSectionQueries(false);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMore = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMore = () => {
    setAnchorEl(null);
  };

  const [openForwardQuery, setForwardQuery] = useState(false);
  const [confirmDailog, setConfirmDailog] = useState(false);
  const [reportQueryId, setReportQueryId] = useState(0);
  const [prevAssign, setPrevAssign] = useState('');
  const [resloveQueryId, setResloveQueryId] = useState<string>('');

  const [currentId, setCurrentId] = useState<string>('');

  const { createQueryResErrorMsg, createQueryResSuccessMsg } = useFieldTranslation();

  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const [createQueryRes, { isLoading: _isCreating, isSuccess: _createSuccess, isError: _createError }] =
    useCreateQueryResponseMutation();

  const [createQueryBulkRes, { isLoading: _isBulkCreating, isSuccess: _bulkCreateSuccess, isError: _bulkCreateError }] =
    useCreateQueryBulkResponseMutation();

  useEffect(() => {
    dispatch(setProgress(_isCreating));
  }, [_isCreating]);

  useEffect(() => {
    dispatch(setProgress(_isBulkCreating));
  }, [_isBulkCreating]);

  useEffect(() => {
    if (_bulkCreateSuccess) {
      afterBulkCreate(createSuccessMessage(), true);
    }
  }, [_bulkCreateSuccess]);

  const getModuleSection = () => {
    return queryKeys.report_category === 'SUSIVRT'
      ? 'SIV REPORT'
      : queryKeys.report_category === 'SQREPT'
      ? 'SQ REPORT'
      : queryKeys.report_category === 'SUMILST'
      ? 'MILESTONE TRACKING'
      : '';
  };

  const afterBulkCreate = (msg: IMessage, _: boolean) => {
    batch(() => {
      getSivReportQueries();
      dispatch(hideLoader());
      dispatch(setMessage(msg));
      setRespondFieldQueries(false);
      setRespondSectionQueries(false);
    });
  };

  useEffect(() => {
    if (_bulkCreateError) {
      afterBulkCreate(createErrorMessage(), false);
    }
  }, [_bulkCreateError]);

  const [resTexts, setResTexs] = useState<any>({});

  const sendQueryRes = async (id: string) => {
    const newObj = { ...resTexts };
    const fieldQueryIds: string[] = [id];
    const sectionQUeryIds: string[] = [id];

    if (respondFieldQueries) {
      fieldQueries.filter((query: any) => {
        if (
          query.query.report_query_type == 'Field' &&
          query.query.report_section_name == queryKeys.report_section_name &&
          query.query.report_is_opened != false
        ) {
          fieldQueryIds.push(query.query.id);
        }
      });
      const reqObj: ICreateQueryBulResponse = {
        query_ids: fieldQueryIds,
        query_response: newObj[id],
        is_opened: true,
        created_by: user
      };

      createQueryBulkRes({
        site_id: selectedSite == null ? '' : selectedSite,
        study_id: studyId,
        country: selectedCountry == null ? '' : selectedCountry,
        application: process.env.REACT_APP_NAME,
        application_section: selectedAppSection,
        application_module_section: getModuleSection(),
        data: reqObj
      });
    } else if (respondSectionQueries) {
      fieldQueries.filter((query: any) => {
        if (
          query.query.report_query_type == 'Section' &&
          query.query.report_section_name == queryKeys.report_section_name &&
          query.query.report_is_opened != false
        ) {
          sectionQUeryIds.push(query.query.id);
        }
      });

      const reqObj: ICreateQueryBulResponse = {
        query_ids: sectionQUeryIds,
        query_response: newObj[id],
        is_opened: true,
        created_by: user
      };
      createQueryBulkRes({
        site_id: selectedSite == null ? '' : selectedSite,
        study_id: studyId,
        country: selectedCountry == null ? '' : selectedCountry,
        application: process.env.REACT_APP_NAME,
        application_section: selectedAppSection,
        application_module_section: getModuleSection(),
        data: reqObj
      });
    } else {
      const reqObj: ICreateQueryResponse = {
        query_response: newObj[id],
        is_opened: true,
        created_by: user
      };
      createQueryRes({
        query_id: id,
        site_id: selectedSite == null ? '' : selectedSite,
        study_id: studyId,
        country: selectedCountry == null ? '' : selectedCountry,
        application: process.env.REACT_APP_NAME,
        application_section: selectedAppSection,
        application_module_section: getModuleSection(),
        data: reqObj
      });
    }
    delete newObj[id];
    setResTexs(newObj);
  };

  useEffect(() => {
    if (_createSuccess) {
      afterCreate(createSuccessMessage(), true);
    }
  }, [_createSuccess]);

  const afterCreate = (msg: IMessage, _: boolean) => {
    batch(() => {
      getSivReportQueries();
      dispatch(hideLoader());
      dispatch(setMessage(msg));
    });
  };

  const createSuccessMessage = (): IMessage => {
    return { msgDetails: createQueryResSuccessMsg, severity: 'success' };
  };

  const createErrorMessage = (): IMessage => {
    return { msgDetails: createQueryResErrorMsg, severity: 'error' };
  };

  useEffect(() => {
    if (_createError) {
      afterCreate(createErrorMessage(), false);
    }
  }, [_createError]);

  const handleTextChange = (e: any, id: string) => {
    const newObj = { ...resTexts };
    newObj[id] = e.target.value;
    setResTexs(newObj);
  };
  const [deleteId, setDeleteId] = useState('');
  const [deleteType, setDeleteType] = useState('');

  const [deleteQuery, { isLoading: _isDeleting, isSuccess: _deleteSuccess, isError: _deleteError }] =
    useDeleteQueryMutation();

  useEffect(() => {
    dispatch(setProgress(_isDeleting));
  }, [_isDeleting]);

  const { deleteQuerySuccessMsg, deleteQueryErrorMsg } = useFieldTranslation();
  useEffect(() => {
    if (_deleteSuccess) {
      afterDelete(deleteSuccessMessage(), true);
    }
  }, [_deleteSuccess]);

  const afterDelete = (msg: IMessage, _: boolean) => {
    batch(() => {
      getSivReportQueries();
      setEnable(false);
      setDeleteId('');
      handleCloseMore();
      dispatch(hideLoader());
      dispatch(setMessage(msg));
    });
  };

  const deleteSuccessMessage = (): IMessage => {
    return { msgDetails: deleteQuerySuccessMsg, severity: 'success' };
  };

  const deleteErrorMessage = (): IMessage => {
    return { msgDetails: deleteQueryErrorMsg, severity: 'error' };
  };

  useEffect(() => {
    if (_deleteError) {
      afterDelete(deleteErrorMessage(), false);
    }
  }, [_deleteError]);

  const handleDeleteFieldQuery = async () => {
    let reqObj = {};
    if (deleteType == 'Field') {
      reqObj = {
        report_query_id: Number(deleteId.split('-')[2]),
        deleted_by: user
      };
    } else {
      reqObj = {
        report_query_id: Number(deleteId.split('-')[2]),
        deleted_by: user
      };
    }
    deleteQuery({
      site_id: selectedSite == null ? '' : selectedSite,
      study_id: studyId,
      country: selectedCountry == null ? '' : selectedCountry,
      application: process.env.REACT_APP_NAME,
      data: reqObj as IDeleteQuery,
      application_section: selectedAppSection,
      application_module_section: getModuleSection()
    });
  };

  const [enable, setEnable] = useState<boolean>(false);

  const handleDialoBox = (id: string) => {
    setEnable(true);
    setDeleteId(id);
  };
  const handleDialogClose = () => {
    setEnable(false);
  };

  const [forwardQueryId, setForwardQueryId] = useState<string>('');

  const handleForwardDialogBox = (id: string, prev_assign: string, forwardId: string) => {
    setForwardQuery(true);
    setReportQueryId(Number(id.split('-')[2]));
    setPrevAssign(prev_assign);
    setForwardQueryId(forwardId);
    setOpen(true);
    setAnchorEl(null);
  };

  const [updateQuery, { isLoading: _isUpdating, isSuccess: _updateSuccess, isError: _updateError }] =
    useUpdateQueryMutation();
  const handleResolveQuery = () => {
    const resQuery = fieldQueries.filter((query: any) => {
      if (query.query.id == resloveQueryId) {
        return query.query;
      }
    });
    let queryId = 0;
    if (resQuery[0].query.report_query_type == 'Field') {
      queryId = Number(resQuery[0].query.report_field_query_id_v.split('-')[2]);
    } else {
      queryId = Number(resQuery[0].query.report_section_query_id_v.split('-')[2]);
    }
    const reqObj = {
      report_category: resQuery[0].query.report_category,
      report_section_name: resQuery[0].query.report_section_name,
      report_field_name: resQuery[0].query.report_field_name,
      report_field_label: resQuery[0].query.report_field_label,
      report_field_value: resQuery[0].query.report_field_value,
      report_query_type: resQuery[0].query.report_query_type,
      report_comment: resQuery[0].query.report_comment,
      report_send_to: resQuery[0].query.report_send_to,
      report_forward_to: resQuery[0].query.report_forward_to,
      report_is_opened: false,
      report_status: resQuery[0].query.report_status,
      report_id: resQuery[0].query.report_id,
      report_query_id: queryId,
      updated_by: user
    };
    updateQuery({
      site_id: selectedSite == null ? '' : selectedSite,
      study_id: studyId,
      country: selectedCountry == null ? '' : selectedCountry,
      application: process.env.REACT_APP_NAME,
      data: reqObj as IUpdateQuery,
      application_section: selectedAppSection,
      application_module_section: getModuleSection()
    });
  };
  useEffect(() => {
    dispatch(setProgress(_isUpdating));
  }, [_isUpdating]);
  useEffect(() => {
    if (_updateSuccess) {
      afterUpdate(updateSuccessMessage(), true);
    }
  }, [_updateSuccess]);
  useEffect(() => {
    if (_updateError) {
      afterUpdate(updateErrorMessage(), false);
    }
  }, [_updateError]);

  const afterUpdate = (msg: IMessage, _: boolean) => {
    batch(() => {
      getSivReportQueries();
      dispatch(hideLoader());
      setConfirmDailog(false);
      dispatch(setMessage(msg));
    });
  };

  const updateSuccessMessage = (): IMessage => {
    return { msgDetails: 'Query Resolved successfully', severity: 'success' };
  };

  const updateErrorMessage = (): IMessage => {
    return { msgDetails: 'Error in resolving query', severity: 'error' };
  };

  const handleRespondAllSectionQueries = (e: any) => {
    setRespondSectionQueries(e.target.checked);
  };

  const handleRespondAlFieldQueries = (e: any) => {
    setRespondFieldQueries(e.target.checked);
  };
  return {
    expanded,
    setExpanded,
    handleChangeAccordion,
    anchorEl,
    setAnchorEl,
    openMore,
    handleClick,
    handleCloseMore,
    resTexts,
    setResTexs,
    sendQueryRes,
    handleTextChange,
    deleteId,
    setDeleteId,
    deleteType,
    setDeleteType,
    handleDeleteFieldQuery,
    enable,
    setEnable,
    handleDialoBox,
    handleDialogClose,
    currentId,
    setCurrentId,
    handleForwardDialogBox,
    openForwardQuery,
    setForwardQuery,
    reportQueryId,
    setReportQueryId,
    confirmDailog,
    setConfirmDailog,
    prevAssign,
    handleResolveQuery,
    setResloveQueryId,
    handleRespondAlFieldQueries,
    handleRespondAllSectionQueries,
    respondFieldQueries,
    respondSectionQueries,
    forwardQueryId
  };
};
